import { useState, useEffect } from 'react';
import styles from './FileCard.module.scss';
import { ResponderType, FileCardData, ChatMessage } from '@chewy/chat-library-common';
import { Bubble } from '@chewy/chat-library-components';
import { KibSpinner } from '@chewy/kib-interstitials-react';
import '@chewy/kib-overlays-styles/dist/styles/kib-spinning-loader.css';
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css';
import { KibInlineAlert } from '@chewy/kib-messaging-react';
import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css';
import '@chewy/kib-messaging-styles/dist/styles/kib-inline-alert.css';
import first from 'lodash/first';

interface FileCardProps {
  data?: FileCardData[];
  message?: ChatMessage<FileCardData>,
}

export default function FileCard({ data, message }: FileCardProps) {
  const fileData = first(data);
  const responseType = message?.responseType as ResponderType || 'user';

  const deriveError = (fd?: FileCardData) =>
    fd?.error || (fd?.status === 'FAILED' ? 'File upload failed' : null);

  const [errorMessage, setErrorMessage] = useState<string | null>(deriveError(fileData));
  const [imageUrl, setImageUrl] = useState<string>(fileData?.url || '');
  const [isLoadingData, setIsLoadingData] = useState<boolean>(fileData?.status === 'UPLOADING');

  useEffect(() => {
    setErrorMessage(deriveError(fileData));
    setImageUrl(fileData?.url || '');
    setIsLoadingData(fileData?.status === 'UPLOADING');
  }, [fileData]);

  const handleImageError = () => {
    setErrorMessage('Failed to load image');
    setIsLoadingData(false);
  };

  return (
    <div className={`${styles.fileCard} ${responseType === 'user' ? styles.rightAligned : styles.leftAligned}`}>
      {isLoadingData || errorMessage ? (
        <div className={styles.spinnerContainer}>
          <Bubble responder={responseType as ResponderType}>
            {isLoadingData ? <KibSpinner />
              : <div className={`${styles.fileInfoContainer}`}>
                <span className={styles.fileInfo}>{`${fileData?.fileName || 'File'}`}</span>
                <span className={styles.fileSize}>{`${(fileData?.byteSize ?? 0) / 1024 / 1024} MB `}</span>
              </div>}
          </Bubble>
          {errorMessage && <KibInlineAlert className={`${styles.errorMessage} ${responseType === 'user' ? styles.rightAligned : styles.leftAligned}`} severity="error">{errorMessage}</KibInlineAlert>}
        </div>
      )
        :
        (
          <img
            src={imageUrl}
            className={styles.fileImage}
            onError={handleImageError}
          />
        )}

    </div>
  );
}
